/**
 * KnowledgeUnitCard — accordion card for a single Knowledge Unit.
 *
 * Collapsed: shows type badge, ordinal, label snippet, layer pills (L1/L2/L3), char count.
 * Expanded:  3-column layout — L1 "Raw content" (60%) | L2 "AI Summary" (20%) | L3 "Extractions" (20%).
 *            Columns not yet available are shown as a muted placeholder.
 *
 * Layer terminology:
 *   L1 — Raw content   : the original text/table/image chunk extracted from the document
 *   L2 — AI Summary    : a condensed summary generated by an AI model
 *   L3 — Extractions   : structured data pulled from the chunk (entities, captions, tables…)
 *
 * L3 extraction rendering:
 *   key_claims         → triple table: Subject → predicate → Object
 *   entity_candidates  → type-grouped colored chips
 *   table_structured   → subject/columns/row-count summary card
 *   others             → fallback raw pre block
 */
import { useState } from 'react';
import { Tag, IconButton } from '@carbon/react';
import { ChevronRight, ChevronDown, Launch, DocumentView } from '@carbon/icons-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useMarkdownComponents } from '@/hooks/useMarkdownComponents';
import { ENTITY_TYPE_GROUPS } from '@shared/constants/knowledge-base';
import type {
  KnowledgeUnit,
  KnowledgeUnitSummary,
  KnowledgeUnitExtraction,
} from '@shared/types/knowledge-base';
import './KnowledgeUnitCard.scss';

interface KnowledgeUnitCardProps {
  unit: KnowledgeUnit;
  /**
   * L2 summary for this unit (1:1). For image units this is a VLM/LLM-generated
   * caption; for all other unit types it is an LLM-condensed text summary.
   * Discriminated by `unit.unit_type` — no extra column needed.
   */
  summary?: KnowledgeUnitSummary;
  extractions?: KnowledgeUnitExtraction[];
  /** When true the card is expanded regardless of local toggle state */
  forceExpanded?: boolean;
  knowledgeObjectId: string;
  /** Called when the user clicks the "Open" button — parent handles the modal */
  onOpen?: () => void;
  /** Called when the user clicks "Locate in PDF" — parent navigates the PDF pane */
  onLocate?: () => void;
  /**
   * Called whenever the card's expand/collapse toggle fires (header click).
   * The parent can use this to sync the PDF viewer to this unit when the pane
   * is already open — without requiring the user to click the DocumentView button.
   */
  onExpand?: () => void;
  /**
   * Layout variant for the expanded body.
   * - 'columns' (default): 3-column side-by-side grid (L1 60% | L2 20% | L3 20%)
   * - 'rows': stacked full-width sections (L1, then L2, then L3) — used in the detail modal
   */
  layout?: 'columns' | 'rows';
}

function unitTypeColor(
  t: KnowledgeUnit['unit_type']
): 'blue' | 'green' | 'magenta' | 'teal' | 'gray' | 'cyan' {
  switch (t) {
    case 'text': return 'blue';
    case 'table': return 'green';
    case 'image': return 'magenta';
    case 'code': return 'teal';
    case 'column': return 'cyan';
    case 'variable': return 'cyan';
    case 'record': return 'gray';
    default: return 'gray';
  }
}

// caption is now rendered in L2, not L3
function extractionKindColor(kind: string): 'purple' | 'warm-gray' | 'red' | 'green' | 'cyan' | 'teal' {
  switch (kind) {
    case 'molecule_smile': return 'purple';
    case 'named_entities': return 'red';
    case 'table_structured': return 'green';
    case 'key_claims': return 'teal';
    case 'entity_candidates': return 'cyan';
    default: return 'warm-gray';
  }
}

/** Layer presence pill shown in the collapsed header */
function LayerPill({ label, title, active }: { label: string; title: string; active: boolean }) {
  return (
    <span
      className={`ku-card__layer-pill${active ? ' ku-card__layer-pill--active' : ''}`}
      title={title}
    >
      {label}
    </span>
  );
}

/** Compact metadata display for structured/timeseries unit types */
function MetadataSnippet({ metadata }: { metadata: Record<string, unknown> }) {
  const keys = ['dtype', 'unit', 'time_range', 'sample_count', 'column_index', 'source_uri'];
  const entries = keys.filter((k) => metadata[k] != null).map((k) => ({ k, v: String(metadata[k]) }));
  if (entries.length === 0) return null;
  return (
    <dl className="ku-card__meta-dl">
      {entries.map(({ k, v }) => (
        <div key={k} className="ku-card__meta-entry">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ─── L3 specialized renderers ─────────────────────────────────────────────────

interface KeyClaim {
  subject: string;
  predicate: string;
  object: string;
}

/** Renders key_claims as a compact subject → predicate → object triple table */
function KeyClaimsRenderer({ extraction }: { extraction: KnowledgeUnitExtraction }) {
  // The worker stores the array directly as structured_data (not nested under a "claims" key).
  const data = extraction.structured_data;
  const claims: KeyClaim[] = Array.isArray(data) ? (data as KeyClaim[]) : [];
  if (claims.length === 0) {
    return <p className="ku-card__col-empty">No claims extracted</p>;
  }
  return (
    <div className="ku-card__claims">
      {claims.map((c, i) => (
        <div key={i} className="ku-card__claim-row">
          <span className="ku-card__claim-subject" title={c.subject}>{c.subject}</span>
          <span className="ku-card__claim-predicate">{c.predicate}</span>
          <span className="ku-card__claim-object" title={c.object}>{c.object}</span>
        </div>
      ))}
    </div>
  );
}

interface EntityCandidate {
  name: string;
  type: string;
  context?: string;
  value?: string;
  unit?: string;
}

/** Renders entity_candidates as type-grouped colored chips */
function EntityCandidatesRenderer({ extraction }: { extraction: KnowledgeUnitExtraction }) {
  // The worker stores the array directly as structured_data (not nested under an "entities" key).
  const data = extraction.structured_data;
  const entities: EntityCandidate[] = Array.isArray(data) ? (data as EntityCandidate[]) : [];
  if (entities.length === 0) {
    return <p className="ku-card__col-empty">No entities extracted</p>;
  }

  // Group entities by their group label using ENTITY_TYPE_GROUPS
  const grouped: Record<string, EntityCandidate[]> = {};
  const ungrouped: EntityCandidate[] = [];

  entities.forEach((e) => {
    let found = false;
    for (const [groupName, groupDef] of Object.entries(ENTITY_TYPE_GROUPS)) {
      if ((groupDef.types as readonly string[]).includes(e.type)) {
        if (!grouped[groupName]) grouped[groupName] = [];
        grouped[groupName].push(e);
        found = true;
        break;
      }
    }
    if (!found) ungrouped.push(e);
  });

  const groupColorClass: Record<string, string> = {
    Physical: 'ku-entity__chip--teal',
    Abstract: 'ku-entity__chip--blue',
    Measurable: 'ku-entity__chip--green',
    Agents: 'ku-entity__chip--purple',
    Document: 'ku-entity__chip--gray',
  };

  return (
    <div className="ku-card__entities">
      {Object.entries(grouped).map(([groupName, groupEntities]) => (
        <div key={groupName} className="ku-entity__group">
          <span className="ku-entity__group-label">{groupName}</span>
          <div className="ku-entity__chips">
            {groupEntities.map((e, i) => {
              // For Metric entities the name already contains the value+unit
              // (LLM puts the full measurement in the name field). Appending
              // value/unit again would duplicate it, so we only add the suffix
              // when the value is NOT already present in the name string.
              const valueSuffix =
                e.value && !e.name.includes(e.value)
                  ? ` ${e.value}${e.unit ? '\u00a0' + e.unit : ''}`
                  : '';
              const label = `${e.name}${valueSuffix}`;
              const colorClass = groupColorClass[groupName] ?? 'ku-entity__chip--gray';
              return (
                <span
                  key={i}
                  className={`ku-entity__chip ${colorClass}`}
                  title={e.context ? `${e.type}: ${e.context}` : e.type}
                >
                  {label}
                  <span className="ku-entity__type-suffix">{e.type}</span>
                </span>
              );
            })}
          </div>
        </div>
      ))}
      {ungrouped.length > 0 && (
        <div className="ku-entity__group">
          <span className="ku-entity__group-label">Other</span>
          <div className="ku-entity__chips">
            {ungrouped.map((e, i) => (
              <span
                key={i}
                className="ku-entity__chip ku-entity__chip--gray"
                title={e.context ? `${e.type}: ${e.context}` : e.type}
              >
                {e.name}
                <span className="ku-entity__type-suffix">{e.type}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Renders table_structured extraction as a summary card */
function TableStructuredRenderer({ extraction }: { extraction: KnowledgeUnitExtraction }) {
  const data = extraction.structured_data as {
    columns?: string[];
    row_count?: number;
    subject?: string;
  } | null;

  return (
    <div className="ku-card__table-structured">
      {extraction.content && (
        <p className="ku-card__table-summary">{extraction.content}</p>
      )}
      {data && (
        <dl className="ku-card__meta-dl" style={{ marginTop: '0.375rem' }}>
          {data.subject && (
            <div className="ku-card__meta-entry"><dt>subject</dt><dd>{data.subject}</dd></div>
          )}
          {data.row_count != null && (
            <div className="ku-card__meta-entry"><dt>rows</dt><dd>{data.row_count}</dd></div>
          )}
          {data.columns && (
            <div className="ku-card__meta-entry">
              <dt>cols</dt>
              <dd>{data.columns.slice(0, 6).join(', ')}{data.columns.length > 6 ? ` +${data.columns.length - 6}` : ''}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

/** Routes to the right renderer based on extraction_kind */
function ExtractionRenderer({ extraction }: { extraction: KnowledgeUnitExtraction }) {
  switch (extraction.extraction_kind) {
    case 'key_claims':
      return <KeyClaimsRenderer extraction={extraction} />;
    case 'entity_candidates':
      return <EntityCandidatesRenderer extraction={extraction} />;
    case 'table_structured':
      return <TableStructuredRenderer extraction={extraction} />;
    default:
      return (
        <>
          {extraction.content && (
            <pre className="ku-card__extraction-content">{extraction.content}</pre>
          )}
          {extraction.structured_data && (
            <pre className="ku-card__extraction-content">
              {JSON.stringify(extraction.structured_data, null, 2)}
            </pre>
          )}
        </>
      );
  }
}

// ─── Main card component ───────────────────────────────────────────────────────

export default function KnowledgeUnitCard({
  unit,
  summary,
  extractions,
  forceExpanded,
  knowledgeObjectId,
  onOpen,
  onLocate,
  onExpand,
  layout = 'columns',
}: KnowledgeUnitCardProps) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = forceExpanded ?? localExpanded;
  const mdComponents = useMarkdownComponents(knowledgeObjectId);

  const l3Extractions = extractions ?? [];
  const hasL2 = !!summary;
  const hasL3 = l3Extractions.length > 0;

  // L1 content block — shared between columns and rows layout
  const l1Content = unit.content ? (
    <>
      {unit.unit_type === 'image' ? (
        <div className="ku-card__image-wrap">
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>
            {unit.content}
          </Markdown>
          {(unit.metadata as any)?.alt_text && (
            <p className="ku-card__image-caption">{(unit.metadata as any).alt_text}</p>
          )}
        </div>
      ) : (
        <div className="ku-card__markdown-body">
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>
            {unit.content}
          </Markdown>
        </div>
      )}
      <MetadataSnippet metadata={unit.metadata} />
    </>
  ) : (
    <p className="ku-card__col-empty">No raw content</p>
  );

  // L2 content block
  const l2Content = hasL2 ? (
    <div className="ku-card__l2-content">
      <div className="ku-card__caption-block">
        <p className="ku-card__caption-text">{summary!.summary}</p>
      </div>
    </div>
  ) : (unit.content_char_count ?? 0) <= 3000 ? (
    <p className="ku-card__col-empty">Below threshold — L1 fits the embedding window directly</p>
  ) : (
    <p className="ku-card__col-empty">Not yet summarised</p>
  );

  // L3 content block
  const l3Content = hasL3 ? (
    <div className="ku-card__extractions">
      {l3Extractions.map((ext) => (
        <div key={ext.id} className="ku-card__extraction">
          <div className="ku-card__extraction-header">
            <Tag type={extractionKindColor(ext.extraction_kind)} size="sm">
              {ext.extraction_kind}
            </Tag>
            {ext.confidence != null && (
              <span className="ku-card__layer-meta">{(ext.confidence * 100).toFixed(0)}%</span>
            )}
            {ext.model_id && (
              <span className="ku-card__layer-meta">{ext.model_id}</span>
            )}
          </div>
          <ExtractionRenderer extraction={ext} />
        </div>
      ))}
    </div>
  ) : (
    <p className="ku-card__col-empty">No extractions yet</p>
  );

  return (
    <div className={`ku-card${isExpanded ? ' ku-card--expanded' : ''}`}>
      {/* ── Collapsed header (always visible) ──────────────────────────── */}
      <div className="ku-card__header-row">
        <button
          className="ku-card__header"
          onClick={() => { setLocalExpanded((v) => !v); onExpand?.(); }}
          aria-expanded={isExpanded}
        >
          <span className="ku-card__chevron">
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>

          <Tag type={unitTypeColor(unit.unit_type)} size="sm" className="ku-card__type-tag">
            {unit.unit_type}
          </Tag>

          <span className="ku-card__ordinal">#{unit.ordinal}</span>

          {unit.label && (
            <span className="ku-card__label" title={unit.label}>
              {unit.label}
            </span>
          )}

          <span className="ku-card__spacer" />

          {/* Layer presence pills — hover shows full name */}
          <LayerPill label="L1" title="L1 — Raw content" active={!!unit.content} />
          <LayerPill label="L2" title="L2 — AI Summary" active={hasL2} />
          <LayerPill label="L3" title="L3 — Extractions" active={hasL3} />

          {unit.content_char_count != null && (
            <span className="ku-card__char-count">
              {unit.content_char_count.toLocaleString()} chars
            </span>
          )}
        </button>

        {/* "Locate in PDF" button — navigates the PDF pane to this unit's page */}
        {onLocate && (
          <IconButton
            kind="ghost"
            size="sm"
            label="Locate in PDF"
            className="ku-card__locate-btn"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onLocate(); }}
          >
            <DocumentView size={14} />
          </IconButton>
        )}

        {/* Open-in-modal button — outside the accordion <button> to avoid nesting */}
        {onOpen && (
          <IconButton
            kind="ghost"
            size="sm"
            label="Open knowledge unit"
            className="ku-card__open-btn"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onOpen(); }}
          >
            <Launch size={14} />
          </IconButton>
        )}
      </div>

      {/* ── Expanded body ────────────────────────────────────────────────── */}
      {isExpanded && (
        <div className="ku-card__body">
          {layout === 'rows' ? (
            /* ── Row layout: stacked full-width sections ─────────────────── */
            <div className="ku-card__rows">
              {/* L1 */}
              <div className="ku-card__row ku-card__row--l1">
                <div className="ku-card__row-header">
                  <span className="ku-card__layer-badge">L1</span>
                  <span className="ku-card__layer-title">Raw content</span>
                  {unit.content_char_count != null && (
                    <span className="ku-card__layer-meta">{unit.content_char_count.toLocaleString()} chars</span>
                  )}
                </div>
                <div className="ku-card__row-content">{l1Content}</div>
              </div>

              {/* L2 */}
              <div className="ku-card__row ku-card__row--l2">
                <div className="ku-card__row-header">
                  <span className="ku-card__layer-badge ku-card__layer-badge--l2">L2</span>
                  <span className="ku-card__layer-title">
                    {unit.unit_type === 'image' ? 'AI Caption' : 'AI Summary'}
                  </span>
                  {hasL2 && summary!.model_id && (
                    <span className="ku-card__layer-meta">{summary!.model_id}</span>
                  )}
                </div>
                <div className="ku-card__row-content">{l2Content}</div>
              </div>

              {/* L3 */}
              <div className="ku-card__row ku-card__row--l3">
                <div className="ku-card__row-header">
                  <span className="ku-card__layer-badge ku-card__layer-badge--l3">L3</span>
                  <span className="ku-card__layer-title">Extractions</span>
                  {hasL3 && (
                    <span className="ku-card__layer-meta">{l3Extractions.length} item{l3Extractions.length > 1 ? 's' : ''}</span>
                  )}
                </div>
                <div className="ku-card__row-content">{l3Content}</div>
              </div>
            </div>
          ) : (
            /* ── Column layout (default): 3-col side-by-side ─────────────── */
            <>
              <div className="ku-card__col-headers">
                <div className="ku-card__col-header ku-card__col-header--l1">
                  <span className="ku-card__layer-badge">L1</span>
                  <span className="ku-card__layer-title">Raw content</span>
                  {unit.content_char_count != null && (
                    <span className="ku-card__layer-meta">{unit.content_char_count.toLocaleString()} chars</span>
                  )}
                </div>
                <div className="ku-card__col-header ku-card__col-header--l2">
                  <span className="ku-card__layer-badge ku-card__layer-badge--l2">L2</span>
                  <span className="ku-card__layer-title">
                    {unit.unit_type === 'image' ? 'AI Caption' : 'AI Summary'}
                  </span>
                  {hasL2 && summary!.model_id && (
                    <span className="ku-card__layer-meta">{summary!.model_id}</span>
                  )}
                </div>
                <div className="ku-card__col-header ku-card__col-header--l3">
                  <span className="ku-card__layer-badge ku-card__layer-badge--l3">L3</span>
                  <span className="ku-card__layer-title">Extractions</span>
                  {hasL3 && (
                    <span className="ku-card__layer-meta">{l3Extractions.length} item{l3Extractions.length > 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
              <div className="ku-card__cols">
                <div className="ku-card__col ku-card__col--l1">{l1Content}</div>
                <div className="ku-card__col ku-card__col--l2">{l2Content}</div>
                <div className="ku-card__col ku-card__col--l3">{l3Content}</div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Made with Bob
